import React, { useEffect, useRef, useState } from 'react';
import { FaSearch, FaSyncAlt } from 'react-icons/fa';
import axiosInstance from '../../utility/axiosInstance';
import Modal from '../Modal';
import ResultGrid from './ResultGrid';
import {
    DataTable, FIND_DELAY_MS, FIND_MAX_CHARS, PAGE_SIZE, REQUEST_TIMEOUT_MS, classifyDataError, defaultFindColumn, lookupUrl, pageUrl,
    readPage, searchUrl,
} from './dataView';
import { ForeignKey } from './rowForm';

/**
 * Related rows (DESIGN_EDITING.md E27): the row a foreign key points at, and a list to pick one from. Both read only;
 * every value is the customer's own and is rendered as text.
 */

type Rows = { status: 'loading' } | { status: 'failed'; line: string } | { status: 'ready'; columns: string[]; rows: (string | null)[][] };

/** Related row: the referenced row, field by field, read with lookup; Open table selects that table with Find set. */
export const RelatedRowModal: React.FC<{
    databaseId: number;
    fk: ForeignKey;
    values: string[];
    onOpenTable: (fk: ForeignKey, values: string[]) => void;
    onClose: () => void;
}> = ({ databaseId, fk, values, onOpenTable, onClose }) => {
    const [state, setState] = useState<Rows>({ status: 'loading' });
    useEffect(() => {
        let active = true;
        const url = lookupUrl(databaseId, fk.ref_schema, fk.ref_table);
        if (!url) { setState({ status: 'failed', line: 'This table cannot be read here.' }); return; }
        axiosInstance.post(url, { columns: fk.ref_columns, values }, { timeout: REQUEST_TIMEOUT_MS })
            .then((res) => {
                if (!active) return;
                const d = res.data;
                if (!d || !Array.isArray(d.columns) || !Array.isArray(d.rows)) { setState({ status: 'failed', line: 'Could not read the related row.' }); return; }
                setState({ status: 'ready', columns: d.columns, rows: d.rows });
            })
            .catch((err) => { if (active) setState({ status: 'failed', line: classifyDataError(err).message }); });
        return () => { active = false; };
    }, [databaseId, fk, values]);

    const where = fk.ref_columns.map((c, i) => `${c} = ${values[i]}`).join(', ');
    const tableName = fk.ref_schema === 'public' ? fk.ref_table : `${fk.ref_schema}.${fk.ref_table}`;
    return (
        <Modal isOpen onClose={onClose} title="Related row" size="md">
            <div className="db-data-form db-data-related">
                <p className="db-data-related-where"><strong>{tableName}</strong> · {where}</p>
                {state.status === 'loading' ? <div className="loading-state"><FaSyncAlt className="spin" /> Reading...</div>
                    : state.status === 'failed' ? <p className="db-data-field-line" role="alert">{state.line}</p>
                    : state.rows.length === 0 ? <p className="db-data-field-line">No row has this value.</p>
                    : state.rows.length === 1 ? (
                        <dl className="db-data-related-fields">
                            {state.columns.map((c, i) => (
                                <React.Fragment key={i}>
                                    <dt>{c}</dt>
                                    <dd>{state.rows[0][i] === null ? <span className="db-data-null">NULL</span> : state.rows[0][i]}</dd>
                                </React.Fragment>
                            ))}
                        </dl>
                    ) : <ResultGrid label={`Rows of ${tableName}`} columns={state.columns} rows={state.rows} emptyText="No rows." truncatedText={null} />}
                <div className="modal-actions">
                    <button type="button" className="cancel-button" onClick={onClose}>Close</button>
                    <button type="button" className="submit-button" onClick={() => onOpenTable(fk, values)}>Open {tableName}</button>
                </div>
            </div>
        </Modal>
    );
};

/** Pick: a page of the referenced table with Find; choosing a row gives its referenced column's text. */
export const RelatedPickerModal: React.FC<{
    databaseId: number;
    fk: ForeignKey;
    /** The referenced table from the table list (for Find's column), when it is listed. */
    refTable: DataTable | null;
    onPick: (value: string) => void;
    onClose: () => void;
}> = ({ databaseId, fk, refTable, onPick, onClose }) => {
    const [state, setState] = useState<Rows>({ status: 'loading' });
    const [text, setText] = useState('');
    const [applied, setApplied] = useState('');
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
    const findColumn = defaultFindColumn(refTable?.columns ?? []) ?? fk.ref_columns[0];
    const refColumn = fk.ref_columns[0];

    useEffect(() => {
        let active = true;
        const q = applied.trim();
        const call = q
            ? axiosInstance.post(searchUrl(databaseId, fk.ref_schema, fk.ref_table)!, { limit: PAGE_SIZE, offset: 0, filter: { column: findColumn, contains: q.slice(0, FIND_MAX_CHARS) } }, { timeout: REQUEST_TIMEOUT_MS })
            : axiosInstance.get(pageUrl(databaseId, fk.ref_schema, fk.ref_table)!, { params: { limit: PAGE_SIZE, offset: 0 }, timeout: REQUEST_TIMEOUT_MS });
        call.then((res) => {
            if (!active) return;
            const page = readPage(res.data);
            if (!page) { setState({ status: 'failed', line: 'Could not read this table.' }); return; }
            setState({ status: 'ready', columns: page.columns, rows: page.rows });
        }).catch((err) => { if (active) setState({ status: 'failed', line: classifyDataError(err).message }); });
        return () => { active = false; };
    }, [databaseId, fk, applied, findColumn]);

    const type = (v: string) => {
        setText(v);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setApplied(v), FIND_DELAY_MS);
    };
    const tableName = fk.ref_schema === 'public' ? fk.ref_table : `${fk.ref_schema}.${fk.ref_table}`;
    const ready = state.status === 'ready' ? state : null;
    const at = ready ? ready.columns.indexOf(refColumn) : -1;
    return (
        <Modal isOpen onClose={onClose} title={`Pick from ${tableName}`} size="lg">
            <div className="db-data-form db-data-related">
                <div className="instance-search-bar db-data-filter">
                    <FaSearch className="instance-search-icon" />
                    <input
                        type="search"
                        className="instance-search-input"
                        aria-label={`Find in ${findColumn}`}
                        placeholder={`Find in ${findColumn}`}
                        value={text}
                        maxLength={FIND_MAX_CHARS}
                        autoFocus
                        onChange={(e) => type(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { if (timer.current) clearTimeout(timer.current); setApplied(text); } }}
                    />
                </div>
                {state.status === 'loading' ? <div className="loading-state"><FaSyncAlt className="spin" /> Reading...</div>
                    : state.status === 'failed' ? <p className="db-data-field-line" role="alert">{state.line}</p>
                    : at < 0 ? <p className="db-data-field-line" role="alert">This table has no column {refColumn}.</p>
                    : (
                        <ResultGrid
                            label={`Rows of ${tableName}`}
                            columns={state.columns}
                            rows={state.rows}
                            emptyText={applied.trim() ? 'No match.' : 'No rows.'}
                            truncatedText={null}
                            onRowClick={(r) => { const v = state.rows[r][at]; if (v !== null) onPick(v); }}
                            rowClickable={(r) => state.rows[r][at] !== null}
                        />
                    )}
                <div className="modal-actions">
                    <button type="button" className="cancel-button" onClick={onClose}>Cancel</button>
                </div>
            </div>
        </Modal>
    );
};
